import { BaseCard } from "./common/BaseCard";
import { MatchScore } from "./MatchScore";
import { cn } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "@/utils/dateFormatter";

export interface Match {
  id: string;
  candidateName: string;
  candidateId: string;
  jobId: string;
  jobTitle: string;
  companyId: string;
  companyName: string;
  score: number;
  description: string;
  strengths: string[];
  status?: string;
  createdAt: string;
}

interface MatchCardProps {
  match: Match;
  className?: string;
}

export const MatchCard = ({
  match,
  className
}: MatchCardProps) => {
  const navigate = useNavigate();
  const headerActions = <MatchScore score={match.score} size="lg" showText={true} />;
  const footerContent = <div className="flex justify-end">
    <Link 
      to={`/matches/${match.id}`} 
      className="inline-flex items-center justify-center px-4 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors dark:bg-blue-500 dark:hover:bg-blue-600"
      onClick={e => e.stopPropagation()}
    >
      View
    </Link>
  </div>;

  return <div onClick={() => navigate(`/matches/${match.id}`)} style={{ cursor: 'pointer' }}>
    <BaseCard 
      id={match.id} 
      title={match.candidateName} 
      className={cn(
        "overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-lg bg-white/80 dark:bg-black/60 border border-white/30 dark:border-white/20 rounded-xl",
        "hover:bg-white/90 dark:hover:bg-black/70 hover:scale-[1.02] hover:border-white/40 dark:hover:border-white/30",
        className
      )} 
      headerActions={headerActions} 
      footerContent={footerContent}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <span className="text-blue-600 font-medium block dark:text-blue-400">
            {match.jobTitle}
          </span>
          <span className="text-sm text-gray-800 block dark:text-gray-200">
            {match.companyName}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
            {match.status || 'New'}
          </span>
          <span className="text-gray-600 dark:text-gray-400">
            {formatDate(match.createdAt)}
          </span>
        </div>
        <p className="text-gray-600 dark:text-gray-300">{match.description}</p>
        {match.strengths && match.strengths.length > 0 && <div>
          <h4 className="text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">Key strengths:</h4>
          <ul className="list-disc pl-5 text-sm space-y-1 text-gray-600 dark:text-gray-300">
            {match.strengths.map((strength, index) => <li key={index}>{strength}</li>)}
          </ul>
        </div>}
      </div>
    </BaseCard>
  </div>;
};
